/**
 * Monatsverlauf als Balkendiagramm mit Nulllinie.
 *
 * Bewusst von Hand gezeichnet statt mit einer Diagramm-Bibliothek: Es sind
 * drei Balken je Monat auf einer gemeinsamen Skala, und 200 kB Fremdcode wären
 * dafür unverhältnismäßig.
 *
 * Die Nulllinie ist der Kern. Vorher rechnete der Server Prozenthöhen und
 * klemmte negative Gewinne auf null – ein Verlustmonat sah aus wie ein Monat
 * ohne Gewinn. Jänner mit −15.900 € war ein winziger roter Stummel über null,
 * also schlicht falsch. Jetzt wächst der Gewinnbalken nach unten, wenn der
 * Monat einen Verlust brachte, und die Achse sagt, wie tief.
 */

import { eurKurz } from '../hilfen/format';
import './monatsdiagramm.css';

export interface Monatswert {
  monat: string;
  label: string;
  umsatz: number;
  ausgaben: number;
  gewinn: number;
  umsatzText: string;
  ausgabenText: string;
  gewinnText: string;
  gewinnKurz: string;
}

/**
 * Rundet die Achse auf einen glatten Wert auf.
 *
 * Ohne das endet die Achse bei 168.412 € und niemand kann eine Balkenhöhe
 * überschlagen. Gesucht wird die nächstgrößere Zahl aus 1/2/5 mal Zehnerpotenz.
 */
function glatt(wert: number): number {
  if (wert <= 0) return 0;
  const stelle = Math.pow(10, Math.floor(Math.log10(wert)));
  for (const faktor of [1, 2, 5, 10]) {
    if (wert <= faktor * stelle) return faktor * stelle;
  }
  return 10 * stelle;
}

export function Monatsdiagramm({ monate }: { monate: Monatswert[] }) {
  if (monate.length === 0) {
    return (
      <div className="dia-leer">
        Noch keine Monatszahlen erfasst. Sie kommen aus den Buchhaltungsberichten.
      </div>
    );
  }

  const hoechster = Math.max(0, ...monate.map((m) => Math.max(m.umsatz, m.ausgaben, m.gewinn)));
  const tiefster = Math.min(0, ...monate.map((m) => Math.min(m.gewinn, 0)));

  const oben = glatt(hoechster) || 1;
  const unten = -glatt(Math.abs(tiefster));
  const spanne = oben - unten;

  /** Anteil der Fläche oberhalb der Nulllinie – daran hängt die ganze Geometrie. */
  const anteilOben = oben / spanne;

  /**
   * Achsenmarken in glatten Schritten.
   *
   * Die Schrittweite richtet sich nach der Spanne, damit weder zwei Striche
   * noch zwanzig entstehen.
   */
  const schritt = glatt(spanne / 4) || 1;
  const marken: number[] = [];
  for (let w = Math.ceil(unten / schritt) * schritt; w <= oben + 1; w += schritt) marken.push(w);
  if (!marken.includes(0)) marken.push(0);
  marken.sort((a, b) => b - a);

  /** Position eines Wertes von oben, in Prozent der Zeichenfläche. */
  const vonOben = (wert: number) => ((oben - wert) / spanne) * 100;

  return (
    <div className="dia">
      <div className="dia-flaeche">
        {/* Achse mit Marken – ohne sie ist eine Balkenhöhe nicht lesbar. */}
        <div className="dia-achse">
          {marken.map((w) => (
            <div
              key={w}
              className={`dia-marke ${w === 0 ? 'null' : ''}`}
              style={{ top: `${vonOben(w)}%` }}
            >
              <span className="dia-marke-text">{w === 0 ? '0' : eurKurz(w)}</span>
              <span className="dia-marke-linie" />
            </div>
          ))}
        </div>

        <div className="dia-monate">
          {monate.map((m) => (
            <div className="dia-monat" key={m.monat}>
              <div className="dia-saeule">
                {/*
                  Jeder Balken sitzt auf der Nulllinie: positive wachsen nach
                  oben, negative nach unten. Deshalb wird jeder einzeln
                  positioniert statt in einer Reihe gestapelt.
                */}
                <Balken wert={m.umsatz} art="umsatz" titel={`Umsatz ${m.umsatzText}`} anteilOben={anteilOben} spanne={spanne} />
                <Balken wert={m.ausgaben} art="ausgaben" titel={`Ausgaben ${m.ausgabenText}`} anteilOben={anteilOben} spanne={spanne} />
                <Balken wert={m.gewinn} art="gewinn" titel={`Gewinn ${m.gewinnText}`} anteilOben={anteilOben} spanne={spanne} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="dia-fuss">
        {monate.map((m) => (
          <div className="dia-fuss-monat" key={m.monat}>
            <span className="dia-fuss-label">{m.label}</span>
            <span className={`dia-fuss-gewinn ${m.gewinn < 0 ? 'minus' : 'plus'}`}>
              {m.gewinn >= 0 ? '+' : ''}
              {m.gewinnKurz}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Ein einzelner Balken, an der Nulllinie verankert. */
function Balken({
  wert,
  art,
  titel,
  anteilOben,
  spanne,
}: {
  wert: number;
  art: 'umsatz' | 'ausgaben' | 'gewinn';
  titel: string;
  anteilOben: number;
  spanne: number;
}) {
  const hoehe = (Math.abs(wert) / spanne) * 100;
  const nulllinie = anteilOben * 100;

  return (
    <div
      className={`dia-balken ${art} ${wert < 0 ? 'negativ' : ''}`}
      title={titel}
      style={
        wert >= 0
          ? { bottom: `${100 - nulllinie}%`, height: `${hoehe}%` }
          : { top: `${nulllinie}%`, height: `${hoehe}%` }
      }
    />
  );
}